import axiosInstance from '../config/axios';
import { toastService } from './toast';
import { ROLES } from './permissionsService';

/**
 * خدمة مزامنة الصلاحيات مع السيرفر
 * تستخدم لتحديث صلاحيات المستخدم من الباك إند
 */

/**
 * تحديث صلاحيات المستخدم الحالي من السيرفر
 * @returns {Promise<boolean>} نجاح أو فشل التحديث
 */
export const syncUserPermissions = async () => {
  try {
    // جلب التوكن من التخزين المحلي
    const token = sessionStorage.getItem('token');
    if (!token) return false;

    // تحديد نوع المستخدم
    const userType = sessionStorage.getItem('userType');
    const userRoleId = sessionStorage.getItem('userRoleId');
    
    // مسار API حسب نوع المستخدم
    // نحدد مسار API لكل نوع مستخدم
    let apiUrl = '/api/user/permissions';
    
    // تحديد المسار المناسب حسب نوع المستخدم
    if (userType === 'admin') {
      apiUrl = '/api/admin/permissions';
    } else if (userRoleId === '2') { // المشرف
      apiUrl = '/api/supervisor/permissions';
    }
    
    // تشخيص عملية المزامنة
    
    // معالجة خاصة للمستخدم العادي (المتدرب)
    // المتدرب العادي له صلاحيات محددة وثابتة
    if (userType === 'user' && userRoleId === '4') {
      // نحاول الاتصال بالسيرفر أولاً، وفي حالة الفشل نستخدم القيم الافتراضية
      try {
        const response = await axiosInstance.get(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 3000 // مهلة قصيرة لتجنب الانتظار الطويل
        });
        
        // إذا نجح الطلب، نستخدم الصلاحيات من السيرفر
        const userPermissions = response.data.permissions || [];
        sessionStorage.setItem('userPermissions', JSON.stringify(userPermissions));
        return true;
      } catch (error) {
        // إذا فشل الطلب، نستخدم الصلاحيات الافتراضية للمتدرب
        const basicUserPermissions = [
          'view_courses',
          'create_review',
          'view_profile',
          'view_enrolled_courses',
          'view_certificates',
          'upload_assignments'
        ];
        sessionStorage.setItem('userPermissions', JSON.stringify(basicUserPermissions));
        return true;
      }
    }
    
    // للمدير والمستخدمين الآخرين، نقوم باستدعاء API للحصول على أحدث الصلاحيات

    // جلب بيانات المستخدم من السيرفر
    const response = await axiosInstance.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    if (response.data) {
      // تحديث دور المستخدم في التخزين المحلي
      if (response.data.role) {
        sessionStorage.setItem('userRole', response.data.role);
      }
      
      // تحديث صلاحيات المستخدم المخصصة
      if (response.data.permissions) {
        sessionStorage.setItem('customPermissions', JSON.stringify(response.data.permissions));
        // حفظ في userPermissions للتوافق مع نظام الصلاحيات الجديد
        sessionStorage.setItem('userPermissions', JSON.stringify(response.data.permissions));
      }
      
      // تحديث صلاحيات الدور
      if (response.data.rolePermissions) {
        sessionStorage.setItem('rolePermissions', JSON.stringify(response.data.rolePermissions));
        
        // إذا لم تكن هناك صلاحيات مخصصة، نستخدم صلاحيات الدور
        if (!response.data.permissions || response.data.permissions.length === 0) {
          sessionStorage.setItem('userPermissions', JSON.stringify(response.data.rolePermissions));
        }
      }
      
      // إطلاق حدث تغيير التخزين المحلي لتحديث السياق
      window.dispatchEvent(new Event('storage'));
      return true;
    }
    
    return false;
  } catch (error) {
    return false;
  }
};

/**
 * تحديث صلاحيات المستخدم بعد تسجيل الدخول
 * يجب استدعاؤها بعد تسجيل الدخول مباشرة
 */
export const initializePermissions = async () => {
  try {
    // يجب التحقق من وجود توكن ومستخدم
    const token = sessionStorage.getItem('token');
    const userObject = sessionStorage.getItem('user');
    
    if (!token || !userObject) {
      return; // لا يوجد مستخدم مسجل دخول
    }
    
    // محاولة مزامنة الصلاحيات من السيرفر
    const result = await syncUserPermissions();
    
    // إذا فشلت المزامنة، نستخدم القيم الافتراضية
    if (!result) {
      // لو فشل التحديث نستخدم القيم الافتراضية من التخزين المحلي
      const userType = sessionStorage.getItem('userType');
      if (userType === 'admin') {
        sessionStorage.setItem('userRole', ROLES.ADMIN);
        
        // إعطاء المدير كل الصلاحيات افتراضياً
        const adminDefaultPermissions = [];
      
        // صلاحيات الوصول للواجهة
        adminDefaultPermissions.push(
          'access_dashboard', // صلاحية دخول لوحة التحكم
          'courses_access', 'users_access', 'devices_access', 
          'payments_access', 'discounts_access', 'books_access',
          'ask_instructor_access', 'reviews_access', 'settings_access'
        );

        // إضافة الصلاحيات التفصيلية
        adminDefaultPermissions.push(
          // الدورات
          'view_courses', 'create_course', 'edit_course', 'delete_course',
          'view_course_content', 'create_course_content', 'edit_course_content', 'delete_course_content',
          'view_exams', 'create_exam', 'edit_exam', 'delete_exam',
          
          // المستخدمين
          'view_admins', 'create_admin', 'edit_admin', 'delete_admin',
          'view_instructors', 'create_instructor', 'edit_instructor', 'delete_instructor',
          'view_trainees', 'create_trainee', 'edit_trainee', 'delete_trainee',
          
          // الأجهزة
          'view_devices', 'manage_devices', 'delete_devices',
          
          // المدفوعات
          'view_bank_transfers', 'verify_bank_transfers', 'delete_bank_transfers',
          'view_electronic_payments', 'manage_electronic_payments', 'delete_electronic_payments',
          
          // الإعدادات
          'view_settings', 'manage_settings'
        );
        
        sessionStorage.setItem('customPermissions', JSON.stringify(adminDefaultPermissions));
      } else {
        sessionStorage.setItem('userRole', ROLES.TRAINEE);
      }
      
      window.dispatchEvent(new Event('storage'));
    }
  } catch (error) {
    // تجاهل الأخطاء في تهيئة الصلاحيات
  }
};

/**
 * تغيير دور مستخدم (للمسؤولين فقط)
 * @param {string} userId معرف المستخدم
 * @param {string} newRole الدور الجديد
 * @returns {Promise<Object>} نتيجة التحديث
 */
export const changeUserRole = async (userId, newRole) => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) throw new Error('غير مسموح بهذه العملية');
    
    const response = await axiosInstance.put(`/api/admin/users/${userId}/role`, {
      role: newRole
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    toastService.success('تم تغيير دور المستخدم بنجاح');
    return response.data;
  } catch (error) {
    toastService.error(error.response?.data?.message || 'حدث خطأ أثناء تغيير دور المستخدم');
    throw error;
  }
};

/**
 * الحصول على جميع الأدوار وصلاحياتها من الخادم
 * @returns {Promise<Array>} قائمة الأدوار وصلاحياتها
 */
export const getRolesWithPermissions = async () => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) throw new Error('غير مسموح بهذه العملية - يجب تسجيل الدخول');
    
    // التأكد من أن المستخدم مدير
    const userType = sessionStorage.getItem('userType');
    if (userType !== 'admin') {
      throw new Error('فقط المديرون يمكنهم عرض الأدوار والصلاحيات');
    }
    
    // التأكد من وجود تفويض وصلاحيات صحيحة قبل طلب الصلاحيات
    
    // مسار الحصول على الأدوار مع الصلاحيات
    // موجود تحت Route::prefix('admin') في api.php
    const response = await axiosInstance.get('/api/admin/roles/permissions', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    // تعديل التعامل مع شكل البيانات المرجعة من الباك إند
    if (response.data && Array.isArray(response.data)) {
      return response.data; // إذا كانت البيانات ترجع مباشرة كمصفوفة
    } else if (response.data && response.data.roles && Array.isArray(response.data.roles)) {
      return response.data.roles; // إذا كانت البيانات مغلفة بكائن roles
    }
    
    return [];
  } catch (error) {
    
    // تفاصيل الخطأ كاملة
    
    toastService.error(error.response?.data?.message || 'حدث خطأ أثناء جلب الأدوار والصلاحيات');
    throw error;
  }
};

/**
 * تحديث صلاحيات الدور في الباك إند
 * @param {string} roleId معرف الدور (مثل 'admin', 'supervisor', إلخ)
 * @param {array} permissions قائمة بالصلاحيات المراد تعيينها للدور
 * @returns {Promise<Object>} نتيجة التحديث
 */
export const saveRolePermissions = async (roleId, permissions) => {
  try {
    // محاولة حفظ صلاحيات الدور

    const token = sessionStorage.getItem('token');
    if (!token) {
      throw new Error('لم يتم العثور على توكن المستخدم');
    }

    // الحصول على كل الصلاحيات المتاحة من قاعدة البيانات
    const permissionsResponse = await axiosInstance.get('/api/admin/permissions/all', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    // استخراج الصلاحيات المتاحة من الاستجابة
    const availablePermissions = permissionsResponse.data.permissions || [];
    const originalPermissions = [...permissions]; // نسخة من الصلاحيات الأصلية
    
    // تصفية الصلاحيات لإرسال فقط الموجودة في قاعدة البيانات
    const filteredPermissions = permissions.filter(perm => 
      availablePermissions.includes(perm)
    );
    
    // الصلاحيات غير الصالحة (الموجودة في الطلب ولكن غير موجودة في قاعدة البيانات)
    const invalidPermissions = originalPermissions.filter(perm => 
      !availablePermissions.includes(perm)
    );
    
    // نتجاهل الصلاحيات غير الموجودة في قاعدة البيانات
    
    // إرسال الصلاحيات المصفاة فقط إلى الـ API
    // التأكد من أن مصفوفة الصلاحيات دائماً محددة
    const response = await axiosInstance.post(`/api/admin/roles/${roleId}/permissions`, {
      permissions: filteredPermissions || [] // إذا كانت filteredPermissions غير محددة، نرسل مصفوفة فارغة
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    // بعد تحديث الصلاحيات، نجلب الأدوار والصلاحيات المحدثة من السيرفر
    try {
      // استدعاء دالة لتحديث بيانات الأدوار والصلاحيات من السيرفر
      await getRolesWithPermissions();
    } catch (refreshError) {
      // نستمر بالرغم من هذا الخطأ، حيث تم تحديث الصلاحيات بالفعل
    }
    
    return {
      success: true,
      message: response.data.message || 'تم تحديث صلاحيات الدور بنجاح',
      data: response.data.permissions || filteredPermissions
    };
    
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'حدث خطأ أثناء تحديث صلاحيات الدور',
    };
  }
};

/**
 * إرفاق خدمة المزامنة بحدث تسجيل الدخول
 * تنفذ مرة واحدة عند بدء التطبيق
 */
export const setupPermissionsSyncService = () => {
  // مزامنة الصلاحيات عند بدء التطبيق إذا كان المستخدم مسجل دخول
  if (sessionStorage.getItem('token')) {
    // تهيئة الصلاحيات للمستخدم الحالي
    const userType = sessionStorage.getItem('userType');
    
    // إذا كان المستخدم مديراً ولم يتم تعيين الدور بعد
    if (userType === 'admin' && !sessionStorage.getItem('userRole')) {
      sessionStorage.setItem('userRole', ROLES.ADMIN);
    }
    
    // إذا كان المستخدم مديراً ولم يكن لديه صلاحيات مخصصة
    if (userType === 'admin' && !sessionStorage.getItem('customPermissions')) {
      // إعطاء المدير كل الصلاحيات
      const allDefaultPermissions = [];
      
      // صلاحيات الوصول للواجهة
      allDefaultPermissions.push(
        'courses_access', 'users_access', 'devices_access', 
        'payments_access', 'discounts_access', 'books_access',
        'ask_instructor_access', 'reviews_access', 'settings_access'
      );
      
      sessionStorage.setItem('customPermissions', JSON.stringify(allDefaultPermissions));
    }
    
    initializePermissions();
    window.dispatchEvent(new Event('storage'));
  }
  
  // إضافة استماع لأحداث تسجيل الدخول/الخروج
  // إذا كان التطبيق يستخدم نظام أحداث - يمكن استخدامه هنا
  // على سبيل المثال: EventBus.on('userLoggedIn', initializePermissions);
};

/**
 * إضافة دور جديد إلى النظام
 * @param {string} roleName اسم الدور الجديد
 * @returns {Promise<Object>} نتيجة العملية
 */
export const addNewRole = async (roleName) => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) throw new Error('غير مسموح بهذه العملية');
    
    const response = await axiosInstance.post('/api/admin/roles', {
      name: roleName,
      guard_name: 'sanctum'
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    toastService.success('تم إضافة الدور الجديد بنجاح');
    return { success: true, data: response.data };
  } catch (error) {
    toastService.error(error.response?.data?.message || 'حدث خطأ أثناء إضافة الدور الجديد');
    return { success: false };
  }
};

/**
 * حذف دور من النظام
 * @param {string} roleId معرف الدور
 * @returns {Promise<Object>} نتيجة العملية
 */
/**
 * حذف دور من النظام
 * @param {string} roleId معرف الدور
 * @returns {Promise<Object>} نتيجة العملية
 */
export const deleteRole = async (roleId) => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) throw new Error('غير مسموح بهذه العملية');
    
    // التأكد من عدم حذف الأدوار الأساسية
    if (['admin', 'supervisor', 'instructor', 'trainee', 'blocked'].includes(roleId) ||
        ['1', '2', '3', '4', '5'].includes(String(roleId))) {
      toastService.error('لا يمكن حذف الأدوار الأساسية');
      return { success: false };
    }
    
    // للتعامل مع الأدوار المؤقتة (للتوافق مع الإصدارات السابقة)
    if (String(roleId).startsWith('temp_')) {
      toastService.success('تم حذف الدور من الواجهة');
      return { success: true };
    }
    
    // حذف الدور من قاعدة البيانات
    const response = await axiosInstance.delete(`/api/admin/roles/${roleId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    // تم إزالة رسالة التوست من هنا لتجنب التكرار مع الرسالة في SecuritySettings
    return { success: true, data: response.data };
  } catch (error) {
    toastService.error(error.response?.data?.message || 'حدث خطأ أثناء حذف الدور');
    return { success: false };
  }
};

export default {
  syncUserPermissions,
  initializePermissions,
  changeUserRole,
  saveRolePermissions,
  setupPermissionsSyncService,
  getRolesWithPermissions,
  addNewRole,
  deleteRole
};
